import { memo } from 'react';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { WebEDIRowData } from 'support/types/webediTable';
import { CellProps } from 'components/DataTable/cells/types';
import {
  getCellNestedLevel,
  isProcurosAddedValue,
  useCellClassNames,
  useIsReadonly,
} from 'components/DataTable/cells/WebEDI/cellHelpers';
import { NestedLevelAddon } from 'components/DataTable/cells/WebEDI/components/NestedLevelAddon/NestedLevelAddon';
import { SuggestedContentIcon } from 'components/DataTable/cells/WebEDI/components/SuggestedContentIcon/SuggestedContentIcon';
import { InputWrapperCell } from 'components/DataTable/cells/WebEDI/CreateEdit/InputWrapperCell';
import { shouldShowDiff } from 'components/DataTable/helpers/shouldShowDiff';

import { CalculatedText } from './CalculatedText';
import { TextCellInput } from './TextCellInput';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;
// eslint-disable-next-line react/display-name
export const TextCell = memo(
  ({ fieldId, column, row, table, isHighlighted, mode, diffStatus }: Props) => {
    const {
      columnDef: { meta: columnMeta, ...columnDef },
      getIsFirstColumn,
      getIsLastColumn,
    } = column;
    const optionalConfig = columnMeta?.optionalConfig;
    const { service } = useNavigationContext<WebEDIAnnotation>();
    const fieldState = service.useFieldStateFromAnnotations(fieldId);
    const popoverItem = service.useItemByFieldPath(fieldId);

    const isSubmitting = table.options.meta?.isSubmitting ?? false;
    const isAdded = isProcurosAddedValue(row);
    const readOnly = useIsReadonly({
      isAdded,
      mode,
      columnMeta,
      rowIndex: row.index,
      fieldId,
      hasError: fieldState === 'error',
    });
    const disabled = isSubmitting;
    const cellNestedLevel = columnMeta?.showNestLevel ? getCellNestedLevel(row) : 0;
    const cellClassNames = useCellClassNames({
      isLastColumn: getIsLastColumn(),
      isFirstColumn: getIsFirstColumn(),
      row,
      table,
    });

    const hasDiff = shouldShowDiff(diffStatus, fieldState);

    const rightAddon = hasDiff ? <SuggestedContentIcon mode="edit" /> : undefined;

    const leftAddon =
      columnMeta?.showNestLevel && cellNestedLevel > 1 ? (
        <NestedLevelAddon level={cellNestedLevel} className="py-3" />
      ) : null;

    if (optionalConfig?.expression || optionalConfig?.translateValue) {
      return (
        <InputWrapperCell
          fieldId={fieldId}
          isHighlighted={isHighlighted}
          className={cellClassNames}
          meta={columnMeta}
          fieldState={fieldState}
          diffStatus={diffStatus}
        >
          <CalculatedText
            fieldId={fieldId}
            readOnly={readOnly}
            disabled={disabled}
            error={popoverItem}
            cellClassNames={cellClassNames}
            leftAddon={leftAddon}
            rightAddon={rightAddon}
            isAdded={isAdded}
            row={row}
            columnDef={column.columnDef}
          />
        </InputWrapperCell>
      );
    }

    return (
      <InputWrapperCell
        fieldId={fieldId}
        isHighlighted={isHighlighted}
        className={cellClassNames}
        meta={columnMeta}
        fieldState={fieldState}
        diffStatus={diffStatus}
      >
        <TextCellInput
          error={popoverItem}
          quantityField={optionalConfig?.quantityField ?? ''}
          isAdded={isAdded}
          accessorKey={'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
          readOnly={readOnly}
          disabled={disabled}
          leftAddon={leftAddon}
          rightAddon={rightAddon}
          isStatusColumn={Boolean(optionalConfig?.isStatusColumn)}
          cellClassNames={cellClassNames}
          fieldId={fieldId}
          fieldState={fieldState}
        />
      </InputWrapperCell>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.row.id === nextProps.row.id && prevProps.isHighlighted === nextProps.isHighlighted;
  },
);
